import store from '../store';

import DocsDetailType from '../components/docs/DocsDetailType.vue';
import Vue from 'vue';

/**
 * Sort comparer to sort objects by their name in
 * alphabetical order.
 */
const alphabeticalNameComparer = (a, b) => a.name < b.name ? -1 : 1;

export const DOCS_API_PATH = 'docs/api';

export const DocsComponentMixin = {
  props: {
    entry: {
      type: Object,
      required: true
    },
    parent: {
      required: false
    }
  },
  computed: {
    name () {
      return Docs.propertyName(this.entry, this.parent);
    },
    defaultvalue () {
      return Docs.defaultvalue(this.entry);
    },
    description () {
      return Docs.description(this.entry);
    },
    classDescription () {
      return Docs.classDescription(this.entry);
    },
    type () {
      return Docs.type(this.entry);
    },
    nonObjectType () {
      if (this.entry && this.entry.type && this.entry.type.names) {
        if (this.entry.type.names.length > 1) {
          return true;
        }
        if (this.entry.type.names.length === 1 && this.entry.type.names[0] !== 'Object') {
          return true;
        }
      }
      return false;
    },
    deprecatedMessage () {
      // .deprecated may be a string or a boolean (in case of no message)
      if (this.entry && typeof this.entry.deprecated === 'string') {
        return Docs.resolve(this.entry.deprecated);
      }
      return '';
    },
    isDeprecated () {
      return !!(this.entry && this.entry.deprecated);
    },
    isEnum () {
      return this.entry && this.entry.isEnum;
    },
    badges () {
      return Docs.badges(this.entry);
    },
    signature () {
      return Docs.signature(this.entry, false);
    },
    signatureWithParams () {
      return Docs.signatureWithParams(this.entry);
    },
    overviewSignature () {
      return Docs.overviewSignature(this.entry, false);
    },
    longSignature () {
      return Docs.signature(this.entry, true);
    },
    eventSignature () {
      return Docs.eventSignature(this.entry);
    },
    summary () {
      return Docs.summary(this.entry);
    },
    shortSummary () {
      return Docs.shortSummary(this.entry);
    },
    linkTarget () {
      return Docs.linkTarget(this.entry, this.parent);
    },
    namespaces () {
      return Docs.filter(this.entry, { kind: 'namespace' });
    },
    classes () {
      return Docs.filter(this.entry, { kind: 'class' });
    },
    methods () {
      return Docs.filter(this.entry, { kind: 'function', scope: 'instance' });
    },
    staticMethods () {
      return Docs.filter(this.entry, { kind: 'function', scope: 'static' });
    },
    types () {
      return Docs.filter(this.entry, { kind: 'typedef' });
    },
    events () {
      return Docs.filter(this.entry, { kind: 'event' });
    },
    eventType () {
      return this.entry && this.entry.meta && this.entry.meta.code && this.entry.meta.code.value;
    },
    staticProperties () {
      return Docs.filter(this.entry, { kind: ['member', 'constant'], scope: 'static' });
    },
    parameters () {
      if (!this.entry || !this.entry.params) return [];
      return this.entry.params;
    },
    properties () {
      if (!this.entry || !this.entry.properties) {
        if (this.entry && this.entry.kind === 'class') {
          return Docs.filter(this.entry, { kind: ['member', 'constant'], scope: 'instance' });
        }
        return [];
      }
      return this.entry.properties;
    },
    exceptions () {
      if (!this.entry || !this.entry.exceptions) return [];
      return this.entry.exceptions;
    },
    returns () {
      if (!this.entry || !this.entry.returns) return [];
      return this.entry.returns;
    },
    examples () {
      return (this.entry && this.entry.examples) || [];
    }
  }
};

export class Docs {
  static find (spec) {
    const db = store.state.docs.db;
    if (!db) return null;
    return db(spec).get();
  }

  static findErrorByCode (code) {
    const db = store.state.docs.db;
    if (!db) return null;
    return db({ memberof: 'clpp.Error.Code', defaultvalue: code }).first();
  }

  static filter (entry, spec) {
    const db = store.state.docs.db;
    if (!db) return [];
    spec = spec || {};
    spec.memberof = entry.longname;
    const result = db(spec).get().filter(i => !i.hidden);
    return result.sort(alphabeticalNameComparer);
  };

  static getEntryByKey (key) {
    if (!key) return null;

    const { db } = store.state.docs;
    const entry = db().filter({ longname: key }).first();

    return entry || null;
  }

  static getDescription (key) {
    if (!key) return '';

    const db = store.state.docs.db;
    if (db) {
      const [longname, propertyName] = key.split('#');
      const cls = db()
        .filter({
          longname: longname
        })
        .first();
      if (!cls) {
        return `Unfortunately we could not find documentation for ${key}. The ${longname} object was not found!`;
      }

      if (!propertyName) {
        return cls.description;
      }

      const property = cls.properties.filter((p) => p.name === propertyName)[0];
      if (!property) {
        return `Unfortunately we could not find documentation for ${key}. The ${propertyName} property was not found in ${longname}!`;
      }
      return property.description;
    }
    return '';
  }

  static getEvent (name) {
    const db = store.state.docs.db;
    if (db) {
      const r = db().filter({ kind: 'event' }).get().filter((x) => x.meta.code.value === name)[0];
      if (r) {
        return r;
      }
    }
    return '';
  }

  static linkTarget (entry, optParent) {
    if (!entry) return {};
    const name = entry.longname;
    const result = { path: '/docs', query: {} };
    const s = name.split('#');

    if (s.length === 1) {
      // we have no hash in the long name, but there are other
      // things that we should check here.
      //
      // If this is a static function, we can also link to the member
      // if((entry.scope === 'static' && entry.kind === 'function') || entry.kind === 'typedef' || entry.kind === 'constant') {
      const hasNamespace = this.find({ longname: entry.memberof });
      if (hasNamespace && (entry.kind !== 'namespace' && entry.kind !== 'class' && entry.kind !== 'interface')) {
        result.path = `/${DOCS_API_PATH}/${entry.memberof}`;
        result.hash = entry.name;
      } else {
        result.path = `/${DOCS_API_PATH}/${name}`;
      }
    } else {
      // If we see a hash in the long name we are
      // sure that this is sa hashed link and we
      // actually link to the member
      result.path = `/${DOCS_API_PATH}/${entry.memberof}`;
      result.hash = entry.name;
    }

    return result;
  }

  static overviewSignature (entry, params = true) {
    return this.signature(entry, params, true);
  }

  static signatureWithParams (entry) {
    return this.signature(entry, true);
  }

  static signature (entry, params = true, overview = false, typedef = false) {
    if (!entry) return '';
    let name = entry.longname;
    if (entry.kind === 'function' || typedef) {
      if (entry.scope === 'instance') {
        name = entry.name;
      }
      if (!typedef) {
        const method = name.split('#');
        if (method.length > 1) {
          const s = method[0].split('.');
          name = s[s.length - 1] + '.' + method[1];
        }
      } else {
        name = 'function';
      }
      if (!params || !entry.params || !entry.params.length) {
        name += '()';
      } else {
        name += '(' + entry.params.map((p) => p.name).join(', ') + ')';
      }
    } else if (entry.kind === 'member' || (entry.kind === 'constant' && entry.scope === 'instance')) {
      if (entry.scope === 'instance') {
        name = entry.name;
      }

      const method = name.split('#');
      if (method.length > 1) {
        const s = method[0].split('.');
        name = s[s.length - 1] + '.' + method[1];
      }
    } else if (entry.kind === 'class' && !overview) {
      if (!params || !entry.params || !entry.params.length) {
        name += '()';
      } else {
        name += '(' + entry.params.map((p) => p.name).join(', ') + ')';
      };
      name = 'new ' + name;
    } else if (entry.kind === 'event') {
      if (entry.meta && entry.meta.code && entry.meta.code.value) {
        return `${entry.memberof}.${entry.name} = '${entry.meta.code.value}'`;
      }
      return `${entry.memberof}.${entry.name}`;
    } else if (entry.kind === 'constant' && entry.scope === 'static' && !entry.isEnum) {
      if (entry.meta && entry.meta.code && entry.meta.code.value) {
        name += ` = ${entry.meta.code.value}`;
      }
    }
    return name;
  }

  static eventSignature (entry) {
    return `${entry.memberof}.${entry.name}`;
  }

  static shortSummary (entry) {
    if (!entry) return '';
    const s = this.summary(entry);
    if (s) return s;

    const desc = entry.description || entry.classdesc;
    if (desc) {
      return Docs.resolve(desc.split('\n')[0]);
    }
    return '';
  }

  static summary (entry) {
    if (!entry) return '';
    if (entry.summary) return Docs.resolve(entry.summary);
    return '';
  }

  /**
   * Returns the resolved (i.e. @{XXX} replaced) description for
   * a given JSdoc entry
   *
   * @type {Object} entry
   * @return {string}
   */
  static description (entry) {
    if (!entry) return '';
    const desc = entry.description || entry.classdesc;
    if (desc) {
      let proplist = '';

      // Build error data object property list
      if (entry.properties && entry.properties.length > 0) {
        proplist = '<p><ul>';

        entry.properties.forEach(prop => {
          const propDescription = Docs.resolve(Docs.removeP(prop.description));
          proplist += `<li><code>${prop.name}:</code> ${propDescription}</li>`;
        });

        proplist += '</ul></p>';
      }
      return Docs.resolve(desc) + proplist;
    }
    return '';
  }

  /**
   * Returns the resolved (i.e. @{XXX} replaced) description for
   * a given JSdoc entry
   */
  static classDescription (entry) {
    if (!entry) return '';
    const desc = entry.classdesc || entry.description;
    if (desc) {
      return Docs.resolve(desc);
    }
    return '';
  }

  /**
   * Collects and returns an array of badges for a given JSdoc entry.
   */
  static badges (entry) {
    const badges = [];
    if (!entry) return badges;
    if (entry.scope) {
      badges.push(entry.scope);
    }
    if (entry.optional) {
      badges.push('optional');
    }
    if (entry.nullable) {
      badges.push('nullable');
    }
    if (entry.isEnum || entry.kind === 'constant') {
      badges.push('constant');
    }
    return badges;
  }

  /**
   * Takes a JSDocs entry and returns the default value or null.
   *
   * Translates empty string to single quotes and undefined to
   * null.
   */
  static defaultvalue (entry) {
    if (entry.defaultvalue === undefined) return null;
    if (entry.defaultvalue === '') return "''";
    return entry.defaultvalue;
  }

  /**
   * Takes a JSDoc entry and returns the resolved entry
   * types.
   */
  static type (entry) {
    if (!entry || !entry.type || !entry.type.names) return '';

    const links = entry.type.names.map((x) => {
      if (x === 'function' && entry.kind === 'typedef') {
        x = this.signature(entry, true, false, true);
      }
      x = this.replaceGenerics(x);
      return this.link(x);
    });

    return links.join('<span class="docs-uts">|</span>');
  }

  static replaceGenerics (entry) {
    const typesWithGenerics = ['Array', 'Object', 'Promise'];
    for (const type of typesWithGenerics) {
      entry = entry.replace(`[ '${type}' ]`, type);
    }
    return entry;
  }

  /**
   *
   * @param {string} query
   * @param {string} optText
   */
  static link (query, optText) {
    let name = optText || query;

    // Make links for all existent types in arrays etc.
    name = name.replace(/\.<(.*)>/, (m, s) => {
      s = s.replace('(', '').replace(')', '');
      const names = s.split('|');
      let links = names.map(x => {
        x = x.trim();
        let sign = '';
        if (x[0] === '?' || x[0] === '!') {
          sign = x[0];
          x = x.slice(1);
        }
        return sign + this.link(x);
      });
      links = links.join('|');
      if (names.length > 1) {
        links = `(${links})`;
      }

      return `&lt;${links}&gt;`;
    });
    if (query.startsWith('./')) {
      const queryRemovedPrefix = query.slice(1);
      return `<a style="cursor:pointer" onclick="pushRouterPath('${queryRemovedPrefix}')">${name}</a>`;
    }

    const text = `<code>${name}</code>`;
    const [longname, property] = query.split('#');
    const found = this.find({ longname });
    if (found && found.length) {
      const target = found[0];
      if (target) {
        const l = this.linkTarget(target);
        const hash = l.hash || property;
        const h = hash ? '#' + hash : '';
        const docsPath = `${l.path}${h}`;
        return `<a style="cursor:pointer" onclick="pushRouterPath('${docsPath}')">${text}</a>`;
      }
    } else {
      if (longname.indexOf('.') < 0) {
        if (longname.indexOf('HTML') === 0) {
          return `<a href="https://developer.mozilla.org/en-US/docs/Web/API/${longname}">${longname}</a>`;
        } else if (longname === 'void') {
          return '<a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/void">void</a>';
        } else if (longname === '*') {
          return longname;
        }
        // Assume this is a std lib object and link to MDN
        return `<a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/${longname}">${longname}</a>`;
      }
      console.log('Unable to find target', longname);
    }
    return text;
  }

  /**
   * Convert JSDoc links to HTML links, and a whole lot of other stuff.
   *
   * @param {string} value
   * @returns {string}
   */
  static resolve (value) {
    if (!value) return value;

    // Resolves relative links in the shape: "./docs/error"
    value = value.replace(/{@link\s+(\.[^}|]+)(\|[^}]+)?}/g, (match, query, name) => {
      if (name) {
        name = name.substr(1);
      }
      return this.link(query, name);
    });
    // Resolves remaining links
    value = value.replace(/{@link\s+([^}|]+)(\|[^}]+)?}/g, (match, query, name) => {
      if (name) {
        name = name.substr(1);
      }
      return this.link(query, name);
    });
    value = value.replace(/:::\s*(api|api-desc|api-type|api-docs)\s+([^\s+]+)?/g, (match, query, name) => {
      if (query === 'api') {
        const s = name.split('|');
        if (s.length === 2) {
          return this.link(s[0], s[1]);
        }
        return this.link(name);
      } else if (query === 'api-desc') {
        return this.getDescription(name);
      } else if (query === 'api-type') {
        const [longname, property] = name.split('#');
        const result = this.find({ longname: longname });
        if (!result || result.length === 0) {
          return '';
        }
        const entry = result[0];
        if (!property) {
          return this.type(entry);
        } else {
          const prop = entry.properties.filter((p) => p.name === property)[0];
          return this.type(prop);
        }
      } else if (query === 'api-docs') {
        const result = this.find({ longname: name });
        if (!result || result.length === 0) {
          return '';
        }
        const entry = result[0];
        if (entry) {
          let component;
          if (entry.kind === 'typedef' || entry.kind === 'constant') {
            component = 'DocsDetailType';
          } else if (entry.kind === 'namespace') {
            component = 'DocsDetailNamespace';
          } else if (entry.kind === 'class' || entry.kind === 'interface') {
            component = 'DocsDetailClass';
          }
          if (component) {
            const ComponentClass = Vue.extend(DocsDetailType);
            const d = new ComponentClass({
              propsData: { entry: entry }
            });
            d.$mount();
            return d.$el.outerHTML;
          }
        }
        return '';
      }
      return query + ' ' + name;
    });
    return value;
  }

  /**
   * Takes a JSdoc entry and its optional parent
   * and returns the entry name or empty string. If
   * a parent is given and the parent is an enum, the longname
   * of the property will be returned.
   */
  static propertyName (entry, parent) {
    if (!entry) return '';
    if (!parent || !parent.isEnum) return entry.name;
    if (parent && parent.isEnum) {
      return `${parent.name}.${entry.name}`;
    }
    return `${parent.longname}.${entry.name}`;
  }

  /**
   * Remove <p></p> tags from a string
   *
   * @param {string} value
   * @return {string}
   */
  static removeP (value) {
    return value.replace('<p>', '').replace('</p>', '');
  }
}
